// A hull's drawn path, played back on the snapshot clock.
//
// Tanks and warships used to chase their LATEST snapshot: each 20 Hz row
// moved the target, the drawn hull eased after it, and the heading snapped
// straight onto it. Driven at 40 km/h that is a hull that surges and sags
// twenty times a second, and in a turn one that ticks round like a clock
// hand — and the chase camera hangs off that hull, so the whole steppe
// shook behind it (flightsim-37l). Planes never did this: they are drawn
// INTERP_DELAY in the past, on a curve through the snapshots either side of
// the render clock. This is the same playback for hulls.
//
// The rows carry no velocity, so the curve's tangents are read off the
// neighbouring samples (a Catmull-Rom spline on the snapshot times): C1 —
// no kink in the motion at a snapshot, which is what the eye catches. A
// heading or a turret bearing is unwrapped as it is stored, so a hull
// turning through due west doesn't spin the long way round.

// Samples kept per hull: the render clock sits INTERP_DELAY (0.12 s, two
// and a half snapshots) behind the newest, and the spline wants one more on
// either side of the pair it straddles.
export const HULL_HIST = 6;
// A hull the render clock has run past the newest row of (a late packet)
// coasts on along its last segment this long, then holds. Short: a tank
// that really did stop must not slide on through the halt.
export const HULL_COAST_SEC = 0.15;
// A gap between rows wider than this (dropped packets, a hull back in
// sight after fog of war) gets a straight line: spline tangents across a
// hole overshoot.
export const HULL_GAP_SEC = 0.25;
// A jump farther than this between rows is a respawn or a re-spot, not
// driving: the path restarts there instead of sweeping across the map.
export const HULL_JUMP_M = 40;

const TAU = 2 * Math.PI;
const wrap = (a) => a - TAU * Math.round(a / TAU);
const CH = ["x", "y", "z", "yaw", "turYaw", "turPitch"];

/** Append a snapshot row to a hull's history (an array, mutated). */
export function pushHullSample(hist, t, x, y, z, yaw, turYaw, turPitch) {
  let last = hist[hist.length - 1];
  if (last && t < last.t) hist.length = 0; // the clock went backwards: a new session
  else if (last && t === last.t) hist.pop(); // the same tick twice: the newer row wins
  last = hist[hist.length - 1];
  if (last && (x - last.x) ** 2 + (z - last.z) ** 2 > HULL_JUMP_M * HULL_JUMP_M) hist.length = 0;
  last = hist[hist.length - 1];
  hist.push({
    t,
    x,
    y,
    z,
    yaw: last ? last.yaw + wrap(yaw - last.yaw) : yaw,
    turYaw: last ? last.turYaw + wrap(turYaw - last.turYaw) : turYaw,
    turPitch,
  });
  if (hist.length > HULL_HIST) hist.shift();
}

/**
 * The hull's pose at time `t` on the snapshot clock, written into `out`
 * ({x, y, z, yaw, turYaw, turPitch}, bearings wrapped to ±π). Before the
 * first row it holds the first; past the newest it coasts HULL_COAST_SEC.
 */
export function hullPoseAt(hist, t, out) {
  const n = hist.length;
  if (!n) return out;
  if (n === 1 || t <= hist[0].t) {
    copy(hist[0], out);
  } else if (t >= hist[n - 1].t) {
    const b = hist[n - 1];
    const a = hist[n - 2];
    const span = b.t - a.t;
    const dt = Math.min(t - b.t, HULL_COAST_SEC);
    for (const c of CH) out[c] = span > 0 && span < HULL_GAP_SEC ? b[c] + ((b[c] - a[c]) / span) * dt : b[c];
  } else {
    let i = n - 2;
    for (; i > 0 && hist[i].t > t; i--);
    const a = hist[i];
    const b = hist[i + 1];
    const span = b.t - a.t;
    const f = (t - a.t) / span;
    if (span >= HULL_GAP_SEC) {
      for (const c of CH) out[c] = a[c] + (b[c] - a[c]) * f;
    } else {
      // Cubic Hermite with Catmull-Rom tangents (per second, scaled by the
      // segment's span), one-sided at the ends of the history.
      const p = i > 0 && a.t - hist[i - 1].t < HULL_GAP_SEC ? hist[i - 1] : a;
      const q = i + 2 < n && hist[i + 2].t - b.t < HULL_GAP_SEC ? hist[i + 2] : b;
      const f2 = f * f, f3 = f2 * f;
      const h00 = 2 * f3 - 3 * f2 + 1, h10 = f3 - 2 * f2 + f;
      const h01 = 3 * f2 - 2 * f3, h11 = f3 - f2;
      for (const c of CH) {
        const ma = ((b[c] - p[c]) / (b.t - p.t)) * span;
        const mb = ((q[c] - a[c]) / (q.t - a.t)) * span;
        out[c] = h00 * a[c] + h10 * ma + h01 * b[c] + h11 * mb;
      }
    }
  }
  out.yaw = wrap(out.yaw);
  out.turYaw = wrap(out.turYaw);
  return out;
}

function copy(s, out) {
  for (const c of CH) out[c] = s[c];
}
